import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Space, message, Modal, Input, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
  GET_ADJUSTMENT_TASKS,
  EXECUTE_ADJUSTMENT_TASK,
  AUDIT_ADJUSTMENT_TASK,
  CANCEL_ADJUSTMENT_TASK,
  GET_ADJUSTMENT_ATTACHMENT_DOWNLOAD_URL,
  DELETE_ADJUSTMENT_ATTACHMENT
} from '../../graphql/queries/settlement/adjustmentQueries';
import { useTranslation } from 'react-i18next';
import { formatSettlementAmountFromCents } from '../../utils/settlementAmountFormatter';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';

interface AdjustmentRecordListProps {
  billId: string;
  currency: string;
  onRefresh?: () => void;
}

interface AdjustmentTask {
  taskId: string;
  adjustmentType: string;
  adjustmentAmount: string;
  amountBefore: string;
  amountAfter: string;
  feeItemName: string;
  reason: string;
  submitterId: string;
  submitterName: string;
  submittedAt: string;
  status: string;
  /** 调账附件文件名；为空表示无附件 */
  attachmentFileName?: string | null;
}

const AdjustmentRecordList: React.FC<AdjustmentRecordListProps> = ({
  billId,
  currency,
  onRefresh
}) => {
  const { t } = useTranslation();
  const canAudit = hasResource([
    ResourceCode.settlement.SETTLEMENT_COST_BILL_OPERATE,
    ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE,
  ]);
  const [auditModalVisible, setAuditModalVisible] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState<string>('');
  const [auditComment, setAuditComment] = useState('');
  const [auditApproved, setAuditApproved] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, loading, refetch } = useQuery(GET_ADJUSTMENT_TASKS, {
    variables: { billId },
    skip: !billId
  });

  const [executeAdjustmentTask] = useMutation(EXECUTE_ADJUSTMENT_TASK);
  const [auditAdjustmentTask] = useMutation(AUDIT_ADJUSTMENT_TASK);
  const [cancelAdjustmentTask] = useMutation(CANCEL_ADJUSTMENT_TASK);
  const [getAttachmentDownloadUrl] = useLazyQuery(GET_ADJUSTMENT_ATTACHMENT_DOWNLOAD_URL, {
    fetchPolicy: 'network-only'
  });
  const [deleteAdjustmentAttachment] = useMutation(DELETE_ADJUSTMENT_ATTACHMENT);

  useEffect(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  const handleExecute = async (taskId: string) => {
    try {
      await executeAdjustmentTask({
        variables: { taskId }
      });
      message.success('调账任务执行成功');
      refetch();
    } catch (error) {
      console.error('执行调账任务失败:', error);
      message.error('执行调账任务失败: ' + (error as Error).message);
    }
  };

  const handleAudit = async (taskId: string, approved: boolean, comment?: string) => {
    try {
      await auditAdjustmentTask({
        variables: {
          taskId,
          approved,
          auditComment: comment
        }
      });
      message.success(approved ? '调账任务审核通过' : '调账任务审核被驳回');
      refetch();
    } catch (error) {
      console.error('审核调账任务失败:', error);
      message.error('审核调账任务失败: ' + (error as Error).message);
    }
  };

  const handleCancel = async (taskId: string) => {
    Modal.confirm({
      title: '确认取消',
      content: '确定要取消这个调账任务吗？',
      onOk: async () => {
        try {
          await cancelAdjustmentTask({
            variables: {
              taskId,
              reason: '用户手动取消'
            }
          });
          message.success('调账任务已取消');
          refetch();
        } catch (error) {
          console.error('取消调账任务失败:', error);
          message.error('取消调账任务失败: ' + (error as Error).message);
        }
      }
    });
  };

  const handleDownloadAttachment = async (taskId: string) => {
    try {
      message.loading({ content: '正在获取下载链接...', key: 'attachmentDownload' });
      const result = await getAttachmentDownloadUrl({ variables: { taskId } });
      const url = result.data?.getAdjustmentAttachmentDownloadUrl;
      if (!url) {
        message.error({ content: '获取下载链接失败，附件可能已被删除', key: 'attachmentDownload' });
        return;
      }
      message.destroy('attachmentDownload');
      window.open(url, '_blank');
    } catch (error) {
      message.error({ content: '获取下载链接失败: ' + (error as Error).message, key: 'attachmentDownload' });
    }
  };

  const handleDeleteAttachment = (taskId: string, fileName: string) => {
    Modal.confirm({
      title: '确认删除附件',
      content: `确定要删除附件「${fileName}」吗？删除后该调账记录将不再关联附件。`,
      okText: '确认删除',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const result = await deleteAdjustmentAttachment({ variables: { taskId } });
          if (result.data?.deleteAdjustmentAttachment) {
            message.success('附件已删除');
            refetch();
            return;
          }
          message.error('删除附件失败，请刷新后重试');
        } catch (error) {
          message.error('删除附件失败: ' + (error as Error).message);
        }
      }
    });
  };

  const showAuditModal = (taskId: string) => {
    setCurrentTaskId(taskId);
    setAuditModalVisible(true);
    setAuditComment('');
    setAuditApproved(true);
  };

  const handleAuditSubmit = () => {
    handleAudit(currentTaskId, auditApproved, auditComment);
    setAuditModalVisible(false);
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      'INITIATED': { color: 'blue', text: '已发起' },
      'PENDING_AUDIT': { color: 'orange', text: '待审核' },
      'APPROVED': { color: 'green', text: '已通过' },
      'REJECTED': { color: 'red', text: '已驳回' },
      'COMPLETED': { color: 'purple', text: '已完成' },
      'CANCELLED': { color: 'default', text: '已取消' }
    };

    const statusInfo = statusMap[status] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const getAdjustmentTypeTag = (type: string, amount: string) => {
    const amountNum = Number(amount);
    const isIncrease = amountNum > 0;
    return (
      <Tag color={isIncrease ? 'green' : 'red'}>
        {formatSettlementAmountFromCents(Math.abs(amountNum), currency)}
      </Tag>
    );
  };

  const columns = [
    {
      title: '任务ID',
      dataIndex: 'taskId',
      key: 'taskId',
      width: 100,
      render: (taskId: string) => (
        <Button type="link" size="small">
          {taskId.slice(-8)}
        </Button>
      )
    },
    {
      title: '费用项',
      dataIndex: 'feeItemName',
      key: 'feeItemName',
      width: 120,
      ellipsis: true,
      render: (feeItemName: string) => (
        <span title={feeItemName}>{feeItemName}</span>
      )
    },
    {
      title: '调整前金额',
      dataIndex: 'amountBefore',
      key: 'amountBefore',
      width: 120,
      render: (amount: string) => formatSettlementAmountFromCents(amount, currency)
    },
    {
      title: '调整后金额',
      dataIndex: 'amountAfter',
      key: 'amountAfter',
      width: 120,
      render: (amount: string) => formatSettlementAmountFromCents(amount, currency)
    },
    {
      title: '调账金额',
      dataIndex: 'adjustmentAmount',
      key: 'adjustmentAmount',
      width: 120,
      render: (amount: string) => {
        const amountNum = Number(amount);
        const isIncrease = amountNum > 0;
        return (
          <span style={{ color: isIncrease ? '#52c41a' : '#ff4d4f' }}>
            {formatSettlementAmountFromCents(Math.abs(amountNum), currency)}
          </span>
        );
      }
    },
    {
      title: '调账原因',
      dataIndex: 'reason',
      key: 'reason',
      width: 160,
      ellipsis: true,
      render: (reason: string) => (
        <span title={reason}>{reason}</span>
      )
    },
    {
      title: '附件',
      dataIndex: 'attachmentFileName',
      key: 'attachmentFileName',
      width: 220,
      render: (fileName: string | null | undefined, record: AdjustmentTask) => {
        if (!fileName) return '-';
        return (
          <Space size={4}>
            <Tooltip title={fileName}>
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                style={{ padding: 0, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}
                onClick={() => handleDownloadAttachment(record.taskId)}
              >
                {fileName}
              </Button>
            </Tooltip>
            {canAudit && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ padding: 0 }}
                onClick={() => handleDeleteAttachment(record.taskId, fileName)}
              />
            )}
          </Space>
        );
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status)
    },
    {
      title: '操作人员',
      key: 'submitter',
      width: 150,
      render: (record: AdjustmentTask) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ID: {record.submitterId}
          </div>
          <div style={{ fontWeight: 'bold' }}>
            {record.submitterName}
          </div>
        </div>
      )
    },
    {
      title: '提交时间',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 150,
      render: (time: string) => new Date(time).toLocaleString()
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (record: AdjustmentTask) => {
        const actions = [];

        if (record.status === 'PENDING_AUDIT' && canAudit) {
          actions.push(
            <Button
              key="audit"
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              onClick={() => showAuditModal(record.taskId)}
            >
              审核
            </Button>
          );
        }

        if (record.status === 'APPROVED' && canAudit) {
          actions.push(
            <Button
              key="execute"
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              onClick={() => handleExecute(record.taskId)}
            >
              执行
            </Button>
          );
        }

        if (['INITIATED', 'PENDING_AUDIT', 'APPROVED'].includes(record.status) && canAudit) {
          actions.push(
            <Button
              key="cancel"
              danger
              size="small"
              icon={<CloseOutlined />}
              onClick={() => handleCancel(record.taskId)}
            >
              取消
            </Button>
          );
        }

        actions.push(
          <Button
            key="view"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              Modal.info({
                title: '调账任务详情',
                content: (
                  <div>
                    <p><strong>任务ID:</strong> {record.taskId}</p>
                    <p><strong>费用项:</strong> {record.feeItemName}</p>
                    <p><strong>调整前金额:</strong> {formatSettlementAmountFromCents(record.amountBefore, currency)}</p>
                    <p><strong>调整后金额:</strong> {formatSettlementAmountFromCents(record.amountAfter, currency)}</p>
                    <p><strong>调账金额:</strong> {formatSettlementAmountFromCents(Math.abs(Number(record.adjustmentAmount)), currency)}</p>
                    <p><strong>调账原因:</strong> {record.reason}</p>
                    <p><strong>操作人员:</strong> {record.submitterName} (ID: {record.submitterId})</p>
                    <p><strong>状态:</strong> {getStatusTag(record.status)}</p>
                    <p><strong>提交时间:</strong> {new Date(record.submittedAt).toLocaleString()}</p>
                  </div>
                ),
                width: 500
              });
            }}
          >
            详情
          </Button>
        );

        return <Space size="small">{actions}</Space>;
      }
    }
  ];

  const adjustmentTasks = data?.getAdjustmentTasks || [];

  return (
    <div className="max-w-full overflow-hidden">
      <Table
        columns={columns}
        dataSource={adjustmentTasks}
        loading={loading}
        rowKey="taskId"
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => t('table.pagination.totalItems', { total }),
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }
        }}
        size="small"
        scroll={{ x: 1440 }}
        tableLayout="fixed"
      />

      <Modal
        title="审核调账任务"
        open={auditModalVisible}
        onOk={handleAuditSubmit}
        onCancel={() => setAuditModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <label>
            <input
              type="radio"
              checked={auditApproved}
              onChange={() => setAuditApproved(true)}
            />
            {' '}通过
          </label>
          <label style={{ marginLeft: 16 }}>
            <input
              type="radio"
              checked={!auditApproved}
              onChange={() => setAuditApproved(false)}
            />
            {' '}驳回
          </label>
        </div>
        <Input.TextArea
          placeholder="审核意见（可选）"
          value={auditComment}
          onChange={(e) => setAuditComment(e.target.value)}
          rows={3}
          maxLength={500}
        />
      </Modal>
    </div>
  );
};

export default AdjustmentRecordList;
